/**
 * Tipi e costanti condivisi del modulo "Dati commerciali da GO".
 *
 * L'API di Gestionale Open e' in sola lettura: queste pagine non scrivono
 * niente, ne' in GO ne' in BOP.
 */

import { formatCurrency } from '../utils/formatters';

/**
 * I sette database di produzione di Gestionale Open, uno per societa' del
 * gruppo. Serve solo a popolare la tendina in anagrafica Societa: l'autorita'
 * resta il backend, che rivalida il valore prima di ogni chiamata — un nome
 * scelto qui non basta a leggere niente.
 */
export const DATABASE_GO = [
  'arc_bona', 'arc_cap', 'arc_cera', 'arc_pian', 'arc_solu', 'arc_upsr', 'arc_vivi',
] as const;

/** Le quattro viste dell'API, due per ciclo. */
export type VistaGo = 'ricavi' | 'ordini-cliente' | 'costi-fornitore' | 'ordini-fornitore';

export type RaggruppaGo =
  | 'cliente' | 'fornitore' | 'commessa' | 'sottocommessa'
  | 'tipo_documento' | 'articolo' | 'anno' | 'mese' | 'documento';

export type OrdinaGo = 'data_asc' | 'data_desc' | 'importo_desc' | 'importo_asc';

/**
 * Data su cui agiscono periodo e raggruppamenti temporali (`data_su` dell'API).
 * - documento: data del documento;
 * - registrazione: data di registrazione (solo ciclo passivo e ordini fornitore);
 * - competenza: competenza di bilancio, che GO scrive sulla prima nota della
 *   fattura (sui movimenti di commessa: la loro data di competenza).
 */
export type DataSuGo = 'documento' | 'registrazione' | 'competenza';

export const ETICHETTE_DATA_SU: Record<DataSuGo, string> = {
  documento: 'Data documento',
  registrazione: 'Data registrazione',
  competenza: 'Data competenza',
};

/** Etichetta leggibile della colonna dichiarata in `data_riferimento`. */
export function etichettaDataRiferimento(colonna: string | null | undefined): string {
  switch (colonna) {
    case 'DATA_DOCUMENTO': case 'data_documento': return 'data documento';
    case 'DATA_REGISTRAZIONE': case 'data_registrazione': return 'data di registrazione';
    case 'data_competenza_bilancio': return 'competenza di bilancio';
    case 'data_competenza': return 'competenza del movimento';
    default: return colonna ?? '—';
  }
}

/** Periodo dei filtri: un anno ('' = tutti) oppure un intervallo di date. */
export interface PeriodoGo {
  intervallo: boolean;
  anno: string;
  /** YYYY-MM-DD */
  da: string;
  a: string;
  /** Mesi dell'anno scelto (1-12): 1 e 12 = tutto l'anno */
  meseDa: number;
  meseA: number;
  /** '' = la data di default della vista */
  dataSu: DataSuGo | '';
}

/** Aggiunge periodo e data_su ai parametri di una chiamata. */
export function applicaPeriodo(p: URLSearchParams, periodo: PeriodoGo): URLSearchParams {
  if (periodo.intervallo) {
    if (periodo.da) p.set('da_data', periodo.da);
    if (periodo.a) p.set('a_data', periodo.a);
  } else if (periodo.anno && mesiParziali(periodo)) {
    // Solo alcuni mesi dell'anno: l'API filtra per intervallo di date
    const [da, a] = dateDeiMesi(periodo);
    p.set('da_data', da);
    p.set('a_data', a);
  } else if (periodo.anno) {
    p.set('anno', periodo.anno);
  }
  if (periodo.dataSu) p.set('data_su', periodo.dataSu);
  return p;
}

/** Vero se sull'anno scelto e' filtrata solo una parte dei mesi. */
export function mesiParziali(periodo: PeriodoGo): boolean {
  return !periodo.intervallo && !!periodo.anno && (periodo.meseDa !== 1 || periodo.meseA !== 12);
}

/** Primo e ultimo giorno dei mesi scelti dell'anno, come YYYY-MM-DD. */
export function dateDeiMesi(periodo: PeriodoGo): [string, string] {
  const anno = Number(periodo.anno);
  const ultimoGiorno = new Date(Date.UTC(anno, periodo.meseA, 0)).getUTCDate();
  const mm = (m: number) => String(m).padStart(2, '0');
  return [`${anno}-${mm(periodo.meseDa)}-01`, `${anno}-${mm(periodo.meseA)}-${ultimoGiorno}`];
}

/** Descrizione breve del periodo, per titoli e nomi di file. */
export function descriviPeriodo(periodo: PeriodoGo): string {
  if (periodo.intervallo) return `${periodo.da || '…'}_${periodo.a || '…'}`;
  if (mesiParziali(periodo)) return dateDeiMesi(periodo).join('_');
  return periodo.anno || 'tutti-gli-anni';
}

/** Origine di un movimento di commessa (`categoria_origine` della vista movimenti). */
export const ETICHETTE_ORIGINE: Record<string, string> = {
  'fattura vendita': 'Fatture di vendita',
  'fattura acquisto': 'Fatture di acquisto',
  'ddt vendita': 'DDT e bolle di vendita',
  'ddt acquisto': 'DDT di acquisto',
  'contabilita su documento': 'Prima nota di fatture',
  contabilita: 'Prima nota (personale, ratei, rettifiche)',
  magazzino: 'Scarichi di magazzino',
  altro: 'Altro',
  'fattura senza movimento': 'Fatture senza movimento in GO (integrate da BOP)',
};

/**
 * Stato applicativo della risposta.
 * - `NON_MAPPATA`: la societa' non ha un database GO in anagrafica.
 * - `NON_DISPONIBILE`: le viste non sono ancora state create su quel database.
 *   E' lo stato del rollout, non un guasto.
 */
export type StatoGo = 'OK' | 'NON_MAPPATA' | 'NON_DISPONIBILE';

/**
 * Gli importi arrivano come stringhe decimali (`"605755.92"`): sono DECIMAL
 * MySQL, serializzati cosi' per non perdere precisione. Vale anche in
 * `modo=totali`, dove la documentazione dell'API li mostra invece come numeri:
 * il tipo tiene buoni entrambi, e `num()` e `euro()` normalizzano.
 *
 * ⚠️ Non passarli mai a un accessor di ordinamento cosi' come sono: una
 * stringa si ordina alfabeticamente, e "71133.92" finirebbe sopra "605755.92".
 */
export type Importo = string | number;

export interface GruppoTotali {
  codice: string | null;
  etichetta: string | null;
  righe: number;
  documenti: number;
  importo: Importo;
  importo_con_commessa: Importo;
  importo_senza_commessa: Importo;
  importo_autofatture: Importo;
  importo_evaso?: Importo;
  prima_data: string | null;
  ultima_data: string | null;
  // Solo con raggruppa=documento: `codice` e' il progressivo della fattura.
  codice_documento?: string | null;
  descrizione_documento?: string | null;
  serie_documento?: string | null;
  numero_documento?: string | null;
  codice_cliente?: string | null;
  codice_fornitore?: string | null;
}

/** Fatture con una parte non attribuita a commessa (tab "Da collegare"). */
export interface RispostaDaCollegare {
  vista: VistaGo;
  data_riferimento: string;
  database?: string;
  /** Fatture del periodo, collegate e no. */
  documenti_periodo: number;
  importo_periodo: number;
  importo_da_attribuire: number;
  /** Fatture non collegate ma registrate senza conti economici (fatture da ricevere/emettere, anticipi, SAL), escluse dall'elenco. */
  fuori_conto_economico?: { documenti: number; importo: number };
  data: GruppoTotali[];
}

/** Ricavi, costi e margine di un gruppo di movimenti di commessa. Importi gia' numeri. */
export interface MisureCommessa {
  ricavi: number;
  costi: number;
  margine: number;
  /** null se non ci sono ricavi. */
  margine_pct?: number | null;
  movimenti: number;
}

/** Una commessa (o sottocommessa): tutti i suoi movimenti, fatture e non. */
export interface VoceCommessa extends MisureCommessa {
  codice: string | null;
  etichetta: string | null;
  margine_pct: number | null;
}

export interface RispostaCommesse {
  database?: string;
  raggruppa: 'commessa' | 'sottocommessa';
  commessa: string | null;
  fonte: 'movimenti';
  /** Colonna data su cui agisce il periodo (data_competenza di default). */
  data_riferimento: string;
  totali: MisureCommessa;
  /** Lo stesso totale per origine del movimento. */
  origini: Array<MisureCommessa & { origine: string }>;
  /** Solo senza filtro commessa: parte delle fatture non collegata ad alcuna commessa. */
  fatture_non_collegate: { ricavi: number; costi: number } | null;
  /**
   * Parte dei totali presa da fatture con la commessa sulla riga ma senza
   * movimento di commessa in GO (fatture create con le API di GO): BOP le integra.
   */
  integrati?: { ricavi: number; costi: number };
  data: VoceCommessa[];
}

/** Un gruppo del riepilogo di una voce (per origine, conto, mese...). */
export interface GruppoRiepilogo extends MisureCommessa {
  codice: string | null;
  etichetta: string | null;
}

export interface RispostaRiepilogoCommessa {
  database?: string;
  raggruppa: 'origine' | 'conto' | 'mese' | 'costo_ricavo';
  data: GruppoRiepilogo[];
}

/** Un movimento di commessa di GO (vista movimenti-commessa). */
export interface MovimentoCommessa {
  /** null sulle righe di fattura integrate da BOP (integrato_bop = 1) */
  id_movimento: number | null;
  integrato_bop?: number;
  documento_progressivo?: number | null;
  documento_riga?: number | null;
  costo_ricavo: 'costo' | 'ricavo' | string;
  importo: Importo;
  importo_margine: Importo;
  descrizione: string | null;
  codice_articolo: string | null;
  data_competenza: string | null;
  data_registrazione: string | null;
  data_documento: string | null;
  data_riferimento: string | null;
  codice_commessa: string | null;
  codice_sottocommessa: string | null;
  categoria_origine: string;
  documento_tipo: string | null;
  numero_documento: string | null;
  controparte_nome: string | null;
  conto_codice: string | null;
  conto_descrizione: string | null;
}

export interface RispostaMovimenti {
  count: number;
  totale: number;
  has_more: boolean;
  limit: number;
  offset: number;
  data: MovimentoCommessa[];
}

/** Fatture di un lato (vendita o acquisto) di una voce di Analisi commesse. */
export interface FattureVoceCommessa {
  documenti: number;
  /** Parte delle fatture attribuita alla voce, gia' numero. */
  importo: number;
  /** Una riga per fattura: `codice` e' il progressivo, `etichetta` la controparte. */
  data: GruppoTotali[];
}

export interface RispostaDocumentiCommessa {
  database?: string;
  ricavi: FattureVoceCommessa;
  costi: FattureVoceCommessa;
}

export interface TotaleComplessivo {
  righe: number;
  documenti: number;
  importo: Importo;
  importo_con_commessa: Importo;
  importo_autofatture: Importo;
  importo_evaso?: Importo;
}

export interface RispostaTotali {
  success: boolean;
  modo: 'totali';
  vista: VistaGo;
  raggruppa: RaggruppaGo;
  /** Colonna su cui agiscono filtri e raggruppamenti temporali. */
  data_riferimento: string;
  database?: string;
  count: number;
  totale_complessivo: TotaleComplessivo;
  data: GruppoTotali[];
}

/**
 * Una riga e' una riga di documento, non un documento: una fattura con cinque
 * righe produce cinque record. Gli importi arrivano come stringhe decimali
 * (`"582.900000"`): sono DECIMAL MySQL, serializzati cosi' per non perdere
 * precisione. Non sommarli lato client — per le somme c'e' `modo=totali`.
 */
export interface RigaGo {
  progressivo: string | number;
  codice_documento: string | null;
  descrizione_documento: string | null;
  tipo_documento: string | null;
  numero_documento: string | null;
  DATA_DOCUMENTO: string | null;
  DATA_REGISTRAZIONE?: string | null;
  data_riferimento: string | null;
  ragione_sociale_1: string | null;
  codice_cliente?: string | null;
  codice_fornitore?: string | null;
  codice_commessa: string | null;
  descrizione_commessa_1: string | null;
  codice_sottocommessa?: string | null;
  descrizione_sottocommessa?: string | null;
  /** 'movimento' | 'residuo' | 'movimento ddt' | 'riga' | 'testata' | null */
  origine_commessa: string | null;
  /**
   * Solo fatture: una riga ripartita su piu' commesse arriva come un record per
   * movimento commessa di GO (tabella cmm). La chiave di un record e'
   * progressivo + riga + id_movimento_commessa, non progressivo + riga.
   */
  id_movimento_commessa?: number | null;
  /** Importo intero della riga, ripetuto su ogni suo record: non sommarlo. */
  importo_riga_documento?: Importo | null;
  riga: number;
  codice_articolo: string | null;
  DESCRIZIONE1: string | null;
  DESCRIZIONE2: string | null;
  QUANTITA: string | null;
  IMPORTO: Importo | null;
  IMPORTO_EVASO?: Importo | null;
  segno: number;
  importo_con_segno: Importo | null;
  e_autofattura?: number | null;
  /** Solo fatture: competenza di bilancio e da dove viene (contabilita, fattura, registrazione, documento). */
  data_competenza_bilancio?: string | null;
  fonte_competenza?: string | null;
}

export interface RispostaRighe {
  success: boolean;
  modo: 'righe';
  vista: VistaGo;
  data_riferimento: string;
  database?: string;
  count: number;
  totale: number;
  limit: number;
  offset: number;
  has_more: boolean;
  avviso_autofatture?: string;
  data: RigaGo[];
}

/** Involucro delle route BOP: lo stato viaggia accanto ai dati. */
export interface BustaGo<T> {
  success: boolean;
  stato: StatoGo;
  error?: string;
  data: T | null;
}

/** Valore numerico di un importo, per ordinamenti e percentuali. */
export function num(valore: Importo | null | undefined): number {
  if (valore === null || valore === undefined || valore === '') return 0;
  const n = Number(valore);
  return Number.isNaN(n) ? 0 : n;
}

/** Importi in euro nel formato italiano di BOP, da stringa o da numero. */
export function euro(valore: Importo | null | undefined): string {
  if (valore === null || valore === undefined || valore === '') return '—';
  const n = Number(valore);
  return Number.isNaN(n) ? '—' : formatCurrency(n);
}

export function dataIt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const soloData = iso.slice(0, 10);
  const [a, m, g] = soloData.split('-');
  return (a && m && g) ? `${g}/${m}/${a}` : soloData;
}
